import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DocumentTree from '../components/documents/DocumentTree';
import BlockEditor from '../components/documents/BlockEditorEnhanced';
import PresenceAvatars from '../components/documents/PresenceAvatars';
import CommentSidebar from '../components/documents/CommentSidebar';
import CreateDocumentDialog from '../components/documents/CreateDocumentDialog';
import VersionHistory from '../components/documents/VersionHistory';
import DocumentFeedback from '../components/documents/DocumentFeedback';
import FeedbackStats from '../components/documents/FeedbackStats';
import VersionManager from '../components/spaces/VersionManager';
import SpaceDocumentsTransfer from '../components/spaces/SpaceDocumentsTransfer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  Plus,
  FileText,
  Lock,
  Globe,
  Loader2,
  FolderOpen,
  MessageSquare,
  ExternalLink,
  Copy,
  Check,
  History,
  Settings,
  Eye,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { normalizeEditorContentJsonString } from '../lib/editorLinkNormalize';
import { API_URL } from '../config';

const SpaceDetailPage = () => {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { currentOrg, user, token } = useAuth();
  
  // Core state
  const [space, setSpace] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Version state
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [selectedVersionData, setSelectedVersionData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // UI state
  const [showCreateDoc, setShowCreateDoc] = useState(false);
  const [createDocParentId, setCreateDocParentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [autoExpandId, setAutoExpandId] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  
  // Full width from space settings
  const isFullWidth = space?.settings?.full_width || false;
  const [members, setMembers] = useState([]);
  const [publishSettings, setPublishSettings] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const canEdit =
    currentOrg?.user_role && ['company', 'member'].includes(currentOrg.user_role);
  const isViewingVersion = selectedVersionId !== null;

  // Build tree from flat documents (e.g. version snapshots — list of all docs with parent_id)
  const buildTree = useCallback((docs, parentId = null) => {
    return docs
      .filter(d => d.parent_id === parentId)
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map(doc => ({
        ...doc,
        children: buildTree(docs, doc.id)
      }));
  }, []);

  /**
   * GET /spaces/:id/documents returns a nested tree (roots only at top level, each with children[]).
   * Re-running buildTree() on that drops all nested rows — use API shape as-is in that case.
   */
  const normalizeDocumentsInput = useCallback((docs, treeBuilder) => {
    if (!docs || docs.length === 0) return [];
    const allTopLevelRoots = docs.every(
      (d) => d.parent_id == null || d.parent_id === undefined || d.parent_id === ''
    );
    const allHaveChildrenArray = docs.every((d) => Array.isArray(d.children));
    if (allTopLevelRoots && allHaveChildrenArray) {
      return docs;
    }
    return treeBuilder(docs, null);
  }, []);

  // Flatten tree to get first document
  const getFirstDocument = useCallback((tree) => {
    if (!tree || tree.length === 0) return null;
    return tree[0];
  }, []);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    if (!spaceId) return;
    
    try {
      const response = await axios.get(`${API_URL}/spaces/${spaceId}/documents`);
      const treeData = normalizeDocumentsInput(response.data, buildTree);
      setDocuments(treeData);
      return treeData;
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      if (error.response?.status === 403) {
        toast.error("You don't have access to this space");
        navigate('/dashboard/spaces');
      }
      return [];
    }
  }, [spaceId, navigate, buildTree, normalizeDocumentsInput]);

  // Load initial data
  useEffect(() => {
    if (!currentOrg || !spaceId) return;
    
    const loadData = async () => {
      setLoading(true);
      try {
        const [spaceRes, docsRes, membersRes, publishRes] = await Promise.all([
          axios.get(`${API_URL}/organizations/${currentOrg.id}/spaces/${spaceId}`),
          axios.get(`${API_URL}/spaces/${spaceId}/documents`),
          axios.get(`${API_URL}/organizations/${currentOrg.id}/members`),
          axios.get(`${API_URL}/organizations/${currentOrg.id}/spaces/${spaceId}/publish`).catch(() => ({ data: null }))
        ]);
        
        setSpace(spaceRes.data);
        const treeData = normalizeDocumentsInput(docsRes.data, buildTree);
        setDocuments(treeData);
        setMembers(membersRes.data);
        setPublishSettings(publishRes.data);
        
        // Auto-select first document
        const firstDoc = getFirstDocument(treeData);
        if (firstDoc) {
          const docResponse = await axios.get(`${API_URL}/spaces/${spaceId}/documents/${firstDoc.id}`);
          setSelectedDoc(docResponse.data);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        toast.error('Failed to load space');
        navigate('/dashboard/spaces');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [currentOrg?.id, spaceId, navigate, buildTree, normalizeDocumentsInput, getFirstDocument]);

  // Handle version selection
  const handleVersionSelect = async (versionId) => {
    // Clear current state first
    setSelectedDoc(null);
    setDocuments([]);
    
    if (!versionId) {
      // Switching to Live
      setSelectedVersionId(null);
      setSelectedVersionData(null);
      
      try {
        const response = await axios.get(`${API_URL}/spaces/${spaceId}/documents`);
        const treeData = normalizeDocumentsInput(response.data, buildTree);
        setDocuments(treeData);
        
        // Auto-select first document
        const firstDoc = getFirstDocument(treeData);
        if (firstDoc) {
          const docResponse = await axios.get(`${API_URL}/spaces/${spaceId}/documents/${firstDoc.id}`);
          setSelectedDoc(docResponse.data);
        }
      } catch (error) {
        console.error('Failed to load live documents:', error);
      }
      setHasUnsavedChanges(false);
      return;
    }

    // Switching to a version
    setSelectedVersionId(versionId);
    
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${currentOrg.id}/spaces/${spaceId}/versions/${versionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const versionData = response.data;
      const snapshot = versionData.documents_snapshot || [];
      
      setSelectedVersionData(versionData);
      
      // Build tree from snapshot
      const treeData = buildTree(snapshot);
      setDocuments(treeData);
      
      // Auto-select first document
      const firstTreeDoc = getFirstDocument(treeData);
      if (firstTreeDoc) {
        const firstSnapshotDoc = snapshot.find(d => d.id === firstTreeDoc.id);
        if (firstSnapshotDoc) {
          setSelectedDoc(firstSnapshotDoc);
        }
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error('Failed to load version');
      // Revert to Live on error
      setSelectedVersionId(null);
      setSelectedVersionData(null);
      await fetchDocuments();
    }
  };

  // Save current content (Live or Version)
  const handleSave = async () => {
    if (!canEdit) return;
    
    setSaving(true);
    try {
      if (isViewingVersion && selectedVersionData) {
        // Save version documents - flatten tree to snapshot
        const flatDocs = [];
        const flattenTree = (tree, parentId = null) => {
          tree.forEach((doc, idx) => {
            flatDocs.push({
              ...doc,
              parent_id: parentId,
              position: idx,
              children: undefined
            });
            if (doc.children) {
              flattenTree(doc.children, doc.id);
            }
          });
        };
        flattenTree(documents);
        
        // Also include the selected doc with latest content
        const updatedDocs = flatDocs.map(d => 
          d.id === selectedDoc?.id ? { ...d, ...selectedDoc } : d
        );
        
        await axios.put(
          `${API_URL}/organizations/${currentOrg.id}/spaces/${spaceId}/versions/${selectedVersionId}/documents`,
          { documents_snapshot: updatedDocs },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update local version data
        setSelectedVersionData(prev => ({
          ...prev,
          documents_snapshot: updatedDocs
        }));
        
        toast.success('Version saved (changes not visible to visitors until you Publish)');
      } else {
        // Save live document (already handled by BlockEditor)
        toast.success('Saved');
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Handle toggle publish for space
  const handleTogglePublish = async () => {
    if (!currentOrg || !spaceId) return;
    
    setPublishing(true);
    try {
      const newStatus = !publishSettings?.is_published;
      const response = await axios.put(
        `${API_URL}/organizations/${currentOrg.id}/spaces/${spaceId}/publish`,
        { is_published: newStatus }
      );
      setPublishSettings(response.data);
      toast.success(newStatus ? 'Space published!' : 'Space unpublished');
    } catch (error) {
      toast.error('Failed to update publish status');
    } finally {
      setPublishing(false);
    }
  };

  const copyPublishUrl = () => {
    if (publishSettings?.preview_url) {
      navigator.clipboard.writeText(publishSettings.preview_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('URL copied!');
    }
  };

  // Handle space settings change (e.g., full_width)
  const handleSpaceSettingsChange = async (settings) => {
    if (!currentOrg || !spaceId) return;
    
    try {
      const response = await axios.put(
        `${API_URL}/organizations/${currentOrg.id}/spaces/${spaceId}`,
        { settings },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local space state with new settings
      setSpace(prev => ({
        ...prev,
        settings: { ...prev?.settings, ...settings }
      }));
    } catch (error) {
      console.error('Failed to update space settings:', error);
      throw error;
    }
  };

  // Handle full width change from editor
  const handleFullWidthChange = useCallback((isFullWidth) => {
    // Just update space settings, sidebar stays visible
  }, []);

  // Document operations
  const handleCreateDocument = async (title, parentId) => {
    if (!title.trim()) return;

    setSubmitting(true);
    try {
      if (isViewingVersion && selectedVersionData) {
        // Create document in version snapshot
        const newDoc = {
          id: crypto.randomUUID(),
          space_id: spaceId,
          title: title,
          content: '',
          parent_id: parentId,
          position: documents.length,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        
        // Add to snapshot
        const updatedSnapshot = [...selectedVersionData.documents_snapshot, newDoc];
        
        // Persist to database immediately
        await axios.put(
          `${API_URL}/organizations/${currentOrg.id}/spaces/${spaceId}/versions/${selectedVersionId}/documents`,
          { documents_snapshot: updatedSnapshot },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update local state
        setSelectedVersionData(prev => ({
          ...prev,
          documents_snapshot: updatedSnapshot
        }));
        
        // Rebuild tree
        const treeData = buildTree(updatedSnapshot);
        setDocuments(treeData);
        
        setShowCreateDoc(false);
        if (parentId) {
          setAutoExpandId(parentId);
        }
        setCreateDocParentId(null);
        setSelectedDoc(newDoc);
        toast.success('Document created');
      } else {
        // Create in live database
        const response = await axios.post(`${API_URL}/spaces/${spaceId}/documents`, {
          title: title,
          parent_id: parentId,
        });
        setShowCreateDoc(false);
        if (parentId) {
          setAutoExpandId(parentId);
        }
        setCreateDocParentId(null);
        await fetchDocuments();
        setSelectedDoc(response.data);
        toast.success('Document created');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectDocument = async (docId) => {
    if (isViewingVersion && selectedVersionData) {
      // Find document from version snapshot
      const doc = selectedVersionData.documents_snapshot.find(d => d.id === docId);
      if (doc) {
        setSelectedDoc(doc);
      }
    } else {
      try {
        const response = await axios.get(`${API_URL}/spaces/${spaceId}/documents/${docId}`);
        setSelectedDoc(response.data);
      } catch (error) {
        console.error('Failed to fetch document:', error);
        toast.error('Failed to load document');
      }
    }
  };

  const handleUpdateDocument = async (docId, updates) => {
    const updatesNorm =
      typeof updates.content === 'string'
        ? { ...updates, content: normalizeEditorContentJsonString(updates.content) }
        : updates;

    if (isViewingVersion && selectedVersionData) {
      // Update document in version snapshot
      const updatedSnapshot = selectedVersionData.documents_snapshot.map(doc =>
        doc.id === docId ? { ...doc, ...updatesNorm, updated_at: new Date().toISOString() } : doc
      );
      
      // Update local state
      setSelectedVersionData(prev => ({
        ...prev,
        documents_snapshot: updatedSnapshot
      }));
      
      // Update selected doc
      const updatedDoc = updatedSnapshot.find(d => d.id === docId);
      if (updatedDoc) {
        setSelectedDoc(updatedDoc);
      }
      
      // Rebuild tree
      setDocuments(buildTree(updatedSnapshot));
      
      // Persist to database immediately
      try {
        await axios.put(
          `${API_URL}/organizations/${currentOrg.id}/spaces/${spaceId}/versions/${selectedVersionId}/documents`,
          { documents_snapshot: updatedSnapshot },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Saved');
      } catch (error) {
        toast.error('Failed to save version');
      }
    } else {
      // Update live document via API
      try {
        const response = await axios.put(`${API_URL}/spaces/${spaceId}/documents/${docId}`, updatesNorm);
        setSelectedDoc(response.data);
        await fetchDocuments();
        toast.success('Document saved');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to save document');
      }
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document and all its sub-documents?')) return;

    try {
      await axios.delete(`${API_URL}/spaces/${spaceId}/documents/${docId}`);
      if (selectedDoc?.id === docId) {
        setSelectedDoc(null);
      }
      await fetchDocuments();
      toast.success('Document deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleReorderDocuments = async (documentIds, parentId) => {
    try {
      await axios.post(`${API_URL}/spaces/${spaceId}/documents/reorder`, {
        document_ids: documentIds,
        parent_id: parentId,
      });
      await fetchDocuments();
    } catch (error) {
      console.error('Failed to reorder documents:', error);
      toast.error('Failed to reorder documents');
      await fetchDocuments();
    }
  };

  const openCreateDialog = (parentId = null) => {
    setCreateDocParentId(parentId);
    setShowCreateDoc(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!space) {
    return (
      <div className="p-6 md:p-8 lg:p-12">
        <p className="text-slate-600 dark:text-slate-400">Space not found</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-slate-900" data-testid="space-detail-page">
      {/* Top Header Bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
        {/* Left: Back + Space Info */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard/spaces')}
            className="text-slate-600 hover:text-slate-900 dark:text-slate-400"
            data-testid="back-to-spaces-button"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          
          <Separator orientation="vertical" className="h-6" />
          
          <div className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-blue-600" />
            <h1 className="font-semibold text-slate-900 dark:text-white">{space.name}</h1>
            <Badge variant={space.visibility === 'public' ? 'default' : 'secondary'} className="text-xs">
              {space.visibility === 'public' ? (
                <><Globe className="w-3 h-3 mr-1" />Public</>
              ) : (
                <><Lock className="w-3 h-3 mr-1" />Private</>
              )}
            </Badge>
          </div>
        </div>

        {/* Center: Version Manager + Save/Publish */}
        <div className="flex items-center gap-2">
          {canEdit && (
            <VersionManager
              organizationId={currentOrg?.id}
              spaceId={spaceId}
              spaceName={space.name}
              selectedVersionId={selectedVersionId}
              onVersionSelect={handleVersionSelect}
              onSave={handleSave}
              saving={saving}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          )}
        </div>

        {/* Right: JSON transfer + External Link + Settings */}
        <div className="flex items-center gap-2">
          <SpaceDocumentsTransfer
            spaceId={spaceId}
            spaceName={space?.name}
            canEdit={canEdit && !isViewingVersion}
            onImported={async () => {
              setSelectedVersionId(null);
              setSelectedVersionData(null);
              setHasUnsavedChanges(false);
              const tree = await fetchDocuments();
              const first = getFirstDocument(tree || []);
              if (first) {
                try {
                  const docResponse = await axios.get(
                    `${API_URL}/spaces/${spaceId}/documents/${first.id}`
                  );
                  setSelectedDoc(docResponse.data);
                } catch {
                  setSelectedDoc(null);
                }
              } else {
                setSelectedDoc(null);
              }
            }}
          />
          {publishSettings?.is_published && publishSettings?.preview_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(publishSettings.preview_url, '_blank')}
              className="gap-1"
            >
              <ExternalLink className="w-4 h-4" />
              View Site
            </Button>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/dashboard/settings?tab=branding&space=${spaceId}`)}
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Document Tree (Always visible) */}
        <div className="w-64 border-r border-slate-200 dark:border-slate-800 flex flex-col bg-white dark:bg-slate-900">
          {/* Sidebar Header */}
          <div className="p-3 border-b border-slate-200 dark:border-slate-800">
            {canEdit && (
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => openCreateDialog(null)}
                data-testid="new-document-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Document
              </Button>
            )}
          </div>

          {/* Document Tree */}
          <div className="flex-1 overflow-auto p-3">
            <DocumentTree
              documents={documents}
              selectedId={selectedDoc?.id}
              onSelect={handleSelectDocument}
              onDelete={canEdit ? handleDeleteDocument : undefined}
              onCreateChild={canEdit ? openCreateDialog : undefined}
              onReorder={canEdit ? handleReorderDocuments : undefined}
              autoExpandId={autoExpandId}
              onAutoExpandComplete={() => setAutoExpandId(null)}
            />
          </div>
        </div>

        {/* Main Content - Document Editor */}
        <div className="flex-1 bg-slate-50 dark:bg-slate-950 overflow-auto flex flex-col">
          {selectedDoc ? (
            <>
              {/* Document Header */}
              <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <div className="flex items-center gap-3">
                  <PresenceAvatars documentId={selectedDoc.id} currentUserId={user?.id} />
                </div>
                <div className="flex items-center gap-2">
                  {!isViewingVersion && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowVersionHistory(true)}
                      data-testid="open-version-history-button"
                    >
                      <History className="w-4 h-4 mr-2" />
                      History
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Editor */}
              <div className="flex-1 overflow-auto">
                <BlockEditor
                  key={selectedDoc.id}
                  document={selectedDoc}
                  onUpdate={canEdit ? handleUpdateDocument : undefined}
                  readOnly={!canEdit}
                  space={space}
                  members={members}
                  publishSettings={publishSettings}
                  currentOrg={currentOrg}
                  currentUser={user}
                  onPublishChange={handleTogglePublish}
                  onSpaceSettingsChange={handleSpaceSettingsChange}
                  onFullWidthChange={handleFullWidthChange}
                />
                
                {/* Feedback Stats (visible to editors/owners) */}
                {canEdit && (
                  <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800">
                    <FeedbackStats documentId={selectedDoc.id} />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                  {documents.length > 0 ? 'Select a document' : 'No documents yet'}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 max-w-md">
                  {documents.length > 0
                    ? 'Choose a document from the sidebar to view or edit it.'
                    : 'Create your first document to get started.'}
                </p>
                {canEdit && documents.length === 0 && !isViewingVersion && (
                  <Button
                    className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => openCreateDialog(null)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Document
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <CreateDocumentDialog
        isOpen={showCreateDoc}
        onClose={() => {
          setShowCreateDoc(false);
          setCreateDocParentId(null);
        }}
        onSubmit={handleCreateDocument}
        isSubmitting={submitting}
        parentId={createDocParentId}
      />

      {/* Comment Sidebar */}
      {showComments && selectedDoc && (
        <CommentSidebar
          documentId={selectedDoc.id}
          members={members}
          onClose={() => setShowComments(false)}
        />
      )}

      {/* Version History */}
      {showVersionHistory && selectedDoc && (
        <VersionHistory
          documentId={selectedDoc.id}
          onClose={() => setShowVersionHistory(false)}
          onRestore={async () => {
            await fetchDocuments();
            const response = await axios.get(`${API_URL}/spaces/${spaceId}/documents/${selectedDoc.id}`);
            setSelectedDoc(response.data);
          }}
        />
      )}
    </div>
  );
};

export default SpaceDetailPage;
